import React, { useState } from 'react';
import { CiScoreBadge } from './CiScoreBadge';

export default function CIScoreExplainer({ showButton = true }) {
  const [isOpen, setIsOpen] = useState(false);

  if (showButton && !isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="px-4 py-2 rounded-lg border border-[#3EF1C7] bg-[#3EF1C7]/10 text-[#3EF1C7] hover:bg-[#3EF1C7]/20 transition-colors text-sm font-medium"
      >
        ℹ️ What is CI Score?
      </button>
    );
  }

  return (
    <div className={showButton ? "fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" : ""}>
      <div className={showButton ? "rounded-lg border border-[#22304A] bg-[#0E1426] w-full max-w-3xl shadow-[0_0_0_1px_rgba(62,241,199,0.06),0_8px_30px_rgba(3,7,18,0.75)] max-h-[90vh] overflow-y-auto" : "rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6"}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Understanding CI Score</h2>
              <p className="text-[#9CB0C7]">Commerce Index Intelligence Scoring System</p>
            </div>
            {showButton && (
              <button
                onClick={() => setIsOpen(false)}
                className="text-[#9CB0C7] hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* What is CI Score */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">What is CI Score?</h3>
            <p className="text-[#9CB0C7] mb-4">
              CI Score (Commerce Index Score) is an intelligent metric ranging from <strong className="text-white">0 to 1000</strong> that
              evaluates the quality and competitiveness of your products and deals. Think of it as a "credit score" for commerce deals.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-[#111A2E] border border-[#F55454]/20">
                <div className="flex items-center gap-2 mb-2">
                  <CiScoreBadge score={250} />
                  <span className="text-sm font-medium text-[#F55454]">Poor (0-299)</span>
                </div>
                <p className="text-xs text-[#9CB0C7]">Not competitive, high price, low demand</p>
              </div>
              <div className="p-4 rounded-lg bg-[#111A2E] border border-[#FFC857]/20">
                <div className="flex items-center gap-2 mb-2">
                  <CiScoreBadge score={450} />
                  <span className="text-sm font-medium text-[#FFC857]">Average (300-599)</span>
                </div>
                <p className="text-xs text-[#9CB0C7]">Decent deal, moderate competitiveness</p>
              </div>
              <div className="p-4 rounded-lg bg-[#111A2E] border border-[#8CF78E]/20">
                <div className="flex items-center gap-2 mb-2">
                  <CiScoreBadge score={720} />
                  <span className="text-sm font-medium text-[#8CF78E]">Good (600-799)</span>
                </div>
                <p className="text-xs text-[#9CB0C7]">Strong deal, competitive pricing</p>
              </div>
              <div className="p-4 rounded-lg bg-[#111A2E] border border-[#3EF1C7]/20">
                <div className="flex items-center gap-2 mb-2">
                  <CiScoreBadge score={875} />
                  <span className="text-sm font-medium text-[#3EF1C7]">Excellent (800-1000)</span>
                </div>
                <p className="text-xs text-[#9CB0C7]">Outstanding deal, best price, high demand</p>
              </div>
            </div>
          </div>

          {/* How is it Calculated */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">How is CI Score Calculated?</h3>
            <p className="text-[#9CB0C7] mb-4">
              Our AI-powered algorithm analyzes 6 key factors with weighted importance:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#111A2E]">
                <div className="text-[#3EF1C7] font-semibold text-sm w-12">35%</div>
                <div className="flex-1">
                  <div className="text-white font-medium text-sm mb-1">💰 Price Factor</div>
                  <p className="text-xs text-[#9CB0C7]">How your price compares to historical lows. Lower = better score.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#111A2E]">
                <div className="text-[#3EF1C7] font-semibold text-sm w-12">20%</div>
                <div className="flex-1">
                  <div className="text-white font-medium text-sm mb-1">🏆 Competitor Delta</div>
                  <p className="text-xs text-[#9CB0C7]">How your price compares to competitors. Better pricing = higher score.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#111A2E]">
                <div className="text-[#3EF1C7] font-semibold text-sm w-12">15%</div>
                <div className="flex-1">
                  <div className="text-white font-medium text-sm mb-1">📈 Trend Velocity</div>
                  <p className="text-xs text-[#9CB0C7]">Product demand trends. Rising demand = higher score.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#111A2E]">
                <div className="text-[#3EF1C7] font-semibold text-sm w-12">10%</div>
                <div className="flex-1">
                  <div className="text-white font-medium text-sm mb-1">📦 Inventory Signal</div>
                  <p className="text-xs text-[#9CB0C7]">Stock availability. Optimal inventory levels = better score.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#111A2E]">
                <div className="text-[#3EF1C7] font-semibold text-sm w-12">10%</div>
                <div className="flex-1">
                  <div className="text-white font-medium text-sm mb-1">🏷️ Discount Strength</div>
                  <p className="text-xs text-[#9CB0C7]">Discount depth vs MSRP. Deeper discounts = higher score.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#111A2E]">
                <div className="text-[#3EF1C7] font-semibold text-sm w-12">10%</div>
                <div className="flex-1">
                  <div className="text-white font-medium text-sm mb-1">⭐ Merchant Reliability</div>
                  <p className="text-xs text-[#9CB0C7]">Your store's reputation, shipping, and return policies.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Product vs Deal Score */}
          <div className="mb-6 p-4 rounded-lg bg-[#7A5CFF]/10 border border-[#7A5CFF]/20">
            <h3 className="text-lg font-semibold text-white mb-3">🔄 Product CI Score vs Deal CI Score</h3>
            <div className="space-y-3">
              <div>
                <div className="text-[#3EF1C7] font-medium mb-1">Product CI Score</div>
                <p className="text-sm text-[#9CB0C7]">
                  This is the base score for your product without any active deals. It's calculated using current price,
                  historical data, and market conditions.
                </p>
              </div>
              <div>
                <div className="text-[#3EF1C7] font-medium mb-1">Deal CI Score (Higher)</div>
                <p className="text-sm text-[#9CB0C7]">
                  When you create a deal, the score <strong className="text-white">increases</strong> because the discount factor
                  is applied. A 20% discount will boost your CI score significantly, making your deal more attractive to consumers.
                </p>
              </div>
              <div className="p-3 rounded bg-[#111A2E] mt-3">
                <p className="text-xs text-[#9CB0C7] italic">
                  💡 Example: A product with CI Score 720 might jump to 850 when you add a 25% discount,
                  moving it from "Good" to "Excellent" tier.
                </p>
              </div>
            </div>
          </div>

          {/* Why It Matters */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">Why CI Score Matters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-[#111A2E]">
                <div className="text-2xl mb-2">🎯</div>
                <div className="text-white font-medium mb-1">Better Visibility</div>
                <p className="text-xs text-[#9CB0C7]">Higher scores get priority placement in deal feeds</p>
              </div>
              <div className="p-4 rounded-lg bg-[#111A2E]">
                <div className="text-2xl mb-2">💵</div>
                <div className="text-white font-medium mb-1">More Conversions</div>
                <p className="text-xs text-[#9CB0C7]">Scores above 750 see 3x higher conversion rates</p>
              </div>
              <div className="p-4 rounded-lg bg-[#111A2E]">
                <div className="text-2xl mb-2">🚀</div>
                <div className="text-white font-medium mb-1">Wider Distribution</div>
                <p className="text-xs text-[#9CB0C7]">Top deals (800+) get promoted across all channels</p>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="p-4 rounded-lg bg-[#3EF1C7]/10 border border-[#3EF1C7]/20">
            <h4 className="text-white font-semibold mb-2">💡 Tips to Improve Your CI Score</h4>
            <ul className="space-y-2 text-sm text-[#9CB0C7]">
              <li>✓ Offer competitive pricing (check historical lows)</li>
              <li>✓ Create meaningful discounts (15%+ typically boosts score)</li>
              <li>✓ Maintain healthy inventory levels</li>
              <li>✓ Focus on trending product categories</li>
              <li>✓ Build merchant reliability through good service</li>
            </ul>
          </div>

          {showButton && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setIsOpen(false)}
                className="px-6 py-2 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 font-medium"
              >
                Got it!
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
